/**
 * Frontend Template Component - Case Study Page 1
 * 
 * Renders the first page of the case study using JavaScript template literals
 * Matches the backend Jinja2 template exactly for consistent output.
 */

import { escapeHtml, formatInlineBold } from '../utils/inlineTextFormat.js'
import type { CaseStudy, TextSizingConfig } from '../types/caseStudy'
import type { EditorLayout, SectionKey } from '../types/editor'

const getTemplateImagesBaseUrl = (): string => {
  const bucketUrl = (typeof import.meta !== 'undefined' && import.meta.env) 
    ? import.meta.env.VITE_TEMPLATE_IMAGES_BASE_URL 
    : undefined;
  
  if (bucketUrl) {
    return bucketUrl;
  }
  
  return 'https://case-study-prod-template-images-1e62f03a.s3.us-east-2.amazonaws.com';
};

const getSmallSize = (section: string): string => {
  const sizes: Record<string, string> = {
    title: '1.8rem',
    subtitle: '1.0rem',
    at_glance: '0.6rem',
    key_metrics_title: '1.0rem',
    key_metrics_description: '0.6rem',
    challenges: '0.65rem',
    objectives: '0.5rem',
    outcomes: '0.8rem',
    testimonial: '0.65rem'
  };
  return sizes[section] || '0.65rem';
};

const getLargeSize = (section: string): string => {
  const sizes: Record<string, string> = {
    title: '2.6rem',
    subtitle: '1.4rem',
    at_glance: '0.9rem',
    key_metrics_title: '1.4rem',
    key_metrics_description: '0.85rem',
    challenges: '0.85rem',
    objectives: '0.7rem',
    outcomes: '1.0rem',
    testimonial: '0.9rem'
  };
  return sizes[section] || '0.85rem';
};

const getSmallLineHeight = (section: string): string => {
  const lineHeights: Record<string, string> = {
    title: '1.0',
    subtitle: '1.1',
    at_glance: '1.2',
    key_metrics_title: '1.0',
    key_metrics_description: '1.2',
    challenges: '1.2',
    objectives: '1.2',
    outcomes: '1.1',
    testimonial: '1.3'
  };
  return lineHeights[section] || '1.2';
};

const getLargeLineHeight = (section: string): string => {
  const lineHeights: Record<string, string> = {
    title: '1.2',
    subtitle: '1.3',
    at_glance: '1.4',
    key_metrics_title: '1.2',
    key_metrics_description: '1.4',
    challenges: '1.4',
    objectives: '1.4',
    outcomes: '1.3',
    testimonial: '1.5'
  };
  return lineHeights[section] || '1.4';
};

const generateTextSizingCSS = (textSizingConfig: TextSizingConfig = {} as TextSizingConfig): string => {
  if (!textSizingConfig || Object.keys(textSizingConfig).length === 0) {
    return '';
  }

  let cssOverrides = '/* Dynamic sizing overrides */\n:root {\n';
  
  Object.entries(textSizingConfig).forEach(([section, config]) => {
    const cssVar = section.replace(/_/g, '-');
    
    if (config === 'small') {
      cssOverrides += `  --${cssVar}-font-size: ${getSmallSize(section)};\n`;
      cssOverrides += `  --${cssVar}-line-height: ${getSmallLineHeight(section)};\n`;
    } else if (config === 'large') {
      cssOverrides += `  --${cssVar}-font-size: ${getLargeSize(section)};\n`;
      cssOverrides += `  --${cssVar}-line-height: ${getLargeLineHeight(section)};\n`;
    } else if (config === 'auto-fit') {
      const computedKey = `${section}_computed` as keyof typeof textSizingConfig;
      if (textSizingConfig[computedKey]) {
        cssOverrides += `  --${cssVar}-font-size: ${textSizingConfig[computedKey]};\n`;
      }
    } else if (typeof config === 'string' && config.endsWith('rem')) {
      cssOverrides += `  --${cssVar}-font-size: ${config};\n`;
    }
  });

  cssOverrides += '}\n';
  return cssOverrides;
};

const generateSpacingCSS = (layout?: EditorLayout): string => {
  if (!layout?.sectionSpacing) return ''
  let css = ''
  Object.entries(layout.sectionSpacing).forEach(([key, spacing]) => {
    const s = spacing as { marginTop: number; marginBottom: number }
    if (s.marginTop === 0 && s.marginBottom === 0) return
    css += `[data-section-key="${key}"] { margin-top: ${s.marginTop}in !important; margin-bottom: ${s.marginBottom}in !important; }\n`
  })
  return css
}

const PREVIEW_INTERACT_CSS = `
[data-preview-edit] {
  cursor: pointer;
  transition: outline-color 0.12s ease, outline-offset 0.12s ease, background-color 0.12s ease;
  border-radius: 4px;
}
[data-preview-edit]:hover {
  outline: 2px solid rgba(37, 99, 235, 0.6);
  outline-offset: 2px;
  background-color: rgba(37, 99, 235, 0.04);
}
`

interface TemplateDataPage1 {
  title: string;
  subtitle: string;
  at_glance_summary: string;
  key_metrics: Array<{ title: string; description: string }>;
  challenges: string[];
  objectives: string[];
  benefits: Array<{ title: string; description: string }>;
  client_testimonial: {
    quote: string;
    author_name: string;
    author_title: string;
    company: string;
  } | null;
  solutions_text: string;
  services: Array<{ name: string; icon: string }>;
  _objectiveLogos: Record<string, string | null> | null;
}

const convertCaseStudyData = (caseStudy: CaseStudy): TemplateDataPage1 => {
  return {
    title: caseStudy.title,
    subtitle: caseStudy.subtitle,
    at_glance_summary: caseStudy.at_a_glance,
    key_metrics: caseStudy.key_metrics?.map(metric => ({ title: metric.title, description: metric.description })) || [],
    challenges: caseStudy.challenges || [],
    objectives: caseStudy.objectives || [],
    benefits: caseStudy.outcomes?.map(outcome => ({
      title: outcome.title,
      description: outcome.description
    })) || [],
    client_testimonial: (caseStudy.testimonial && caseStudy.testimonial.quote && caseStudy.testimonial.author_name) ? {
      quote: caseStudy.testimonial.quote || "",
      author_name: caseStudy.testimonial.author_name || "",
      author_title: caseStudy.testimonial.author_title || "",
      company: caseStudy.testimonial.company || ""
    } : null,
    solutions_text: 'RISCPoint provides comprehensive cybersecurity solutions tailored to meet your specific compliance and security requirements.',
    services: [
      { name: 'Professional Services', icon: 'professional_services.png' },
      { name: 'Information Technology', icon: 'information_technology.png' },
      { name: 'Account Management', icon: 'account_management.png' }
    ],
    _objectiveLogos: (caseStudy as any)._objectiveLogos ?? null
  };
};

const renderAtAGlanceSection = (data: TemplateDataPage1): string => {
    return `                <div class="at-glance" data-preview-edit="at_a_glance" data-section-key="at_a_glance">
                    <h2>At a Glance</h2>
                    <p>${formatInlineBold(data.at_glance_summary)}</p>
                </div>`
}

const renderKeyMetricsSection = (data: TemplateDataPage1, baseUrl: string): string => {
    return `                <div class="key-metrics" data-section-key="key_metrics" data-preview-edit="key_metrics">
                    <h3>Key Metrics</h3>
                    
                    <!-- Stacked Key Metrics Text Boxes -->
                    <div class="key-metrics-stacked">
                    ${data.key_metrics.map((metric) => `
                        <div class="metric-box">
                            <div class="metric-number-large">${formatInlineBold(metric.title)}</div>
                            <div class="metric-description">${formatInlineBold(metric.description)}</div>
                        </div>
                    `).join('')}
                    </div>

                </div>`
}

const renderTestimonialSection = (data: TemplateDataPage1): string => {
    return data.client_testimonial ? `                <div class="testimonial-section" data-section-key="testimonial">
                    <div class="testimonial-quote">"${formatInlineBold(data.client_testimonial.quote)}"</div>
                    <div class="testimonial-author">— ${formatInlineBold(data.client_testimonial.author_name)}${data.client_testimonial.author_title ? `, ${formatInlineBold(data.client_testimonial.author_title)}` : ''}${data.client_testimonial.company ? `, ${formatInlineBold(data.client_testimonial.company)}` : ''}</div>
                </div>` : ''
}

const renderChallengesSection = (data: TemplateDataPage1): string => {
    return `                <section class="section" data-section-key="challenges">
                    <div class="section-header">
                        <h3>CHALLENGES</h3>
                        <span class="dropdown-arrow">⌄</span>
                    </div>
                    <div class="section-content" data-preview-edit="challenges">
                        <ul>
                        ${data.challenges.map(challenge => `
                            <li>${formatInlineBold(challenge)}</li>
                        `).join('')}
                        </ul>
                    </div>
                </section>`
}

const renderObjectivesSection = (data: TemplateDataPage1, baseUrl: string): string => {
    return `                <section class="section" data-section-key="objectives">
                    <div class="section-header">
                        <h3>OBJECTIVES</h3>
                        <span class="dropdown-arrow">⌄</span>
                    </div>
                    <div class="section-content" data-preview-edit="objectives">
                        <!-- Objectives with Icons -->
                        <div class="objectives-grid">
                        ${data.objectives.slice(0, 3).map((objective, index) => `
                            <div class="objective-item">
                                <div class="objective-icon">
                                    ${(() => {
                                        const userLogos = ['left', 'center', 'right'];
                                        const slot = userLogos[index];
                                        const userLogo = data._objectiveLogos?.[slot];
                                        const userLogoImgStyle = 'width:60px;height:60px;object-fit:contain;';
                                        
                                        if (userLogo) {
                                            return `<div class="service-icon-circle-small">
                                                <img src="${userLogo}" class="service-icon-img-small" style="${userLogoImgStyle}">
                                            </div>`;
                                        } else if (index < data.services.length) {
                                            return `<div class="service-icon-circle-small">
                                                <img src="${baseUrl}/${data.services[index].icon}" alt="${data.services[index].name}" class="service-icon-img-small">
                                            </div>`;
                                        } else {
                                            return `<div style="width: 32px; height: 32px; background: #adb5bd; border-radius: 6px; opacity: 0.8;"></div>`;
                                        }
                                    })()}
                                </div>
                                <div class="objective-text-box">
                                    ${formatInlineBold(objective)}
                                </div>
                            </div>
                        `).join('')}
                        </div>

                    </div>
                </section>`
}

const renderOutcomesSection = (data: TemplateDataPage1, baseUrl: string): string => {
    return `                <section class="section" data-section-key="outcomes">
                    <div class="section-header">
                        <h3>OUTCOMES</h3>
                        <span class="dropdown-arrow">⌄</span>
                    </div>
                    <div class="section-content" data-preview-edit="outcomes">
                        <ul class="benefits-list">
                        ${data.benefits.map((benefit, index) => `
                            <li>
                                <div class="benefit-number">
                                    <img src="${baseUrl}/${index + 1}_block.png" alt="Benefit ${index + 1}" class="benefit-block-img">
                                </div>
                                <div class="benefit-content">
                                    <h4>${formatInlineBold(benefit.title)}</h4>
                                    <p>${formatInlineBold(benefit.description)}</p>
                                </div>
                            </li>
                        `).join('')}
                        </ul>
                    </div>
                </section>`
}

type SectionRenderer = (data: TemplateDataPage1, baseUrl: string) => string

const LEFT_SECTION_RENDERERS: Partial<Record<SectionKey, SectionRenderer>> = {
    at_a_glance: (data) => renderAtAGlanceSection(data),
    key_metrics: (data, baseUrl) => renderKeyMetricsSection(data, baseUrl),
    testimonial: (data) => renderTestimonialSection(data),
}

const RIGHT_SECTION_RENDERERS: Partial<Record<SectionKey, SectionRenderer>> = {
    challenges: (data) => renderChallengesSection(data),
    objectives: (data, baseUrl) => renderObjectivesSection(data, baseUrl),
    outcomes: (data, baseUrl) => renderOutcomesSection(data, baseUrl),
}

export const renderPage1Template = (
  caseStudy: CaseStudy, 
  textSizingConfig: TextSizingConfig = {} as TextSizingConfig,
  layout?: EditorLayout
): string => {
  const templateImagesBaseUrl = getTemplateImagesBaseUrl();
  const dynamicCSS = generateTextSizingCSS(textSizingConfig);
  const spacingCSS = generateSpacingCSS(layout);
  const templateData = convertCaseStudyData(caseStudy);

  const leftSectionKeys: SectionKey[] = layout?.zones?.['page1-left'] || ['at_a_glance', 'key_metrics', 'testimonial']
  const leftHtml = leftSectionKeys
    .filter(key => LEFT_SECTION_RENDERERS[key])
    .map(key => LEFT_SECTION_RENDERERS[key]!(templateData, templateImagesBaseUrl))
    .join('\n')

  const rightSectionKeys: SectionKey[] = layout?.zones?.['page1-right'] || ['challenges', 'objectives', 'outcomes']
  const rightHtml = rightSectionKeys
    .filter(key => RIGHT_SECTION_RENDERERS[key])
    .map(key => RIGHT_SECTION_RENDERERS[key]!(templateData, templateImagesBaseUrl))
    .join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(templateData.title)}</title>
    <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;600;700&display=swap" rel="stylesheet">
    <style>
        /* Dynamic Text Sizing CSS Variables */
        :root {
            /* Default font sizes (medium) */
            --title-font-size: 2.2rem;
            --subtitle-font-size: 1.2rem;
            --at-glance-font-size: 0.75rem;
            --key-metrics-title-font-size: 1.2rem;
            --key-metrics-description-font-size: 0.75rem;
            --challenges-font-size: 0.75rem;
            --objectives-font-size: 0.6rem;
            --outcomes-font-size: 0.9rem;
            --testimonial-font-size: 0.75rem;
            
            /* Line height adjustments based on font size */
            --title-line-height: 1.1;
            --subtitle-line-height: 1.2;
            --at-glance-line-height: 1.3;
            --key-metrics-title-line-height: 1.1;
            --key-metrics-description-line-height: 1.3;
            --challenges-line-height: 1.3;
            --objectives-line-height: 1.3;
            --outcomes-line-height: 1.2;
            --testimonial-line-height: 1.3;
        }
        
        ${dynamicCSS}
        ${spacingCSS}
        ${PREVIEW_INTERACT_CSS}
        
        /* RISCPoint Template Styles */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Work Sans', sans-serif;
            background: #f5f5f5;
            color: #333;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: flex-start;
        }
        
        .container {
            width: 8.5in;
            height: 11in;
            margin: 0;
            background: white;
            box-shadow: 0 0 30px rgba(0,0,0,0.15);
            border-radius: 0;
            overflow: hidden;
            position: relative;
            display: flex;
            flex-direction: column;
        }
        
        .header {
            background: transparent;
            color: #22559c;
            padding: 0.1in 0.3in 0.2in 0.3in;
            position: relative;
            overflow: hidden;
            height: 26%;
            flex-shrink: 0;
        }
        
        .header::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            width: 400px;
            height: 100%;
            background: rgba(255,255,255,0.1);
            clip-path: polygon(20% 0%, 100% 0%, 100% 100%, 0% 100%);
        }
        
        .logo {
            position: absolute;
            top: 5px;
            right: 10px;
            height: 163px;
            width: auto;
            z-index: 3;
            opacity: 0.08;
        }
        
        .header h1 {
            font-size: var(--title-font-size);
            font-weight: 800;
            margin-bottom: 0.05in;
            line-height: var(--title-line-height);
            position: relative;
            z-index: 2;
            max-width: 66%;
            color: #22559c;
            text-shadow: 0 1px 2px rgba(255,255,255,0.8);
            margin-top: 0;
            padding-top: 0;
        }
        
        .header .subtitle {
            font-size: var(--subtitle-font-size);
            font-style: italic;
            position: relative;
            z-index: 2;
            line-height: var(--subtitle-line-height);
            max-width: 66%;
            color: #22559c;
            opacity: 0.8;
        }
        
        .skyline {
            position: absolute;
            top: 5%;
            right: 0;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: right center;
            z-index: 1;
            opacity: 1;
        }
        
        .main-content {
            display: flex;
            height: 67%;
            background: white;
            box-shadow: inset 0 1px 0 rgba(0,0,0,0.1);
            overflow: hidden;
        }
        
        .left-column {
            width: 35%;
            background: white;
            padding: 0.05in 0.1in;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
        }
        
        .at-glance {
            margin-bottom: 0.2in;
            margin-top: 0.1in;
            margin-left: 0.1in;
            flex-shrink: 0;
        }
        
        .at-glance h2 {
            font-size: 1.2rem;
            font-weight: 600;
            color: #333;
            margin-bottom: 0.03in;
            line-height: 1.1;
        }
        
        .at-glance p {
            font-size: var(--at-glance-font-size);
            line-height: var(--at-glance-line-height);
            color: #666;
            margin-bottom: 0.03in;
        }
        
        .key-metrics {
            background: linear-gradient(135deg, #22559c 0%, #006bff 100%);
            color: white;
            padding: 0.1in 0.2in 0.2in 0.3in;
            border-radius: 0 15px 15px 0;
            margin-top: 0.05in;
            margin-left: -0.2in;
            margin-right: 0;
            box-shadow: 0 8px 25px rgba(34,85,156,0.3);
            border: 1px solid rgba(255,255,255,0.1);
            margin-bottom: 0.05in;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            width: calc(85% + 0.3in);
            position: relative;
        }
        
        .key-metrics h3 {
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.05in;
            text-align: left;
        }
        
        .key-metrics-stacked {
            display: flex;
            flex-direction: column;
            gap: 0.05in;
            margin: 0.05in 0 0.1in 0;
        }

        .metric-box {
            background: rgba(255,255,255,0.15);
            border-radius: 8px;
            padding: 0.1in;
            text-align: center;
            border: 1px solid rgba(255,255,255,0.2);
        }

        .metric-number-large {
            font-size: var(--key-metrics-title-font-size);
            font-weight: 700;
            color: white;
            line-height: var(--key-metrics-title-line-height);
        }

        .metric-description {
            font-size: var(--key-metrics-description-font-size);
            color: rgba(255,255,255,0.9);
            margin-top: 0.03in;
            line-height: var(--key-metrics-description-line-height);
        }

        .testimonial-section {
            background: white;
            border-left: 4px solid #22559c;
            padding: 0.15in;
            margin: 0.1in 0;
            border-radius: 0 8px 8px 0;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            margin-top: auto;
            margin-bottom: 0;
        }
        
        .testimonial-quote {
            font-size: var(--testimonial-font-size);
            line-height: var(--testimonial-line-height);
            color: #333;
            font-style: italic;
            margin-bottom: 0.05in;
        }
        
        .testimonial-author {
            font-size: 0.6rem;
            color: #666;
            font-weight: 600;
        }
        
        .right-column {
            flex: 1;
            padding: 0.05in 0.2in;
            background: white;
            overflow: hidden;
            height: 100%;
            display: flex;
            flex-direction: column;
        }
        
        .section {
            margin-bottom: 0.05in;
            padding-bottom: 0.3in;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
        }
        
        .section:last-child {
            border-bottom: none;
            margin-bottom: 0;
        }
        
        .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 0.12in;
            border-bottom: 3px solid #22559c;
            margin-bottom: 0.05in;
            padding-left: 15px;
            padding-right: 15px;
            margin-left: -15px;
            margin-right: -50px;
            padding-top: 0.02in;
        }
        
        .section-header h3 {
            font-size: 1.15rem;
            font-weight: 900;
            color: #1a1a1a;
            letter-spacing: 0.5px;
        }
        
        .dropdown-arrow {
            display: none;
        }
        
        .section-content {
            padding: 0.05in 0;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
        }
        
        .section-content ul {
            list-style: none;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
        }
        
        .section-content li {
            font-size: var(--challenges-font-size);
            line-height: var(--challenges-line-height);
            margin-bottom: 0.01in;
            position: relative;
            padding-left: 15px;
            color: #555;
        }
        
        .section-content li::before {
            content: "•";
            position: absolute;
            left: 0;
            color: #22559c;
            font-weight: bold;
        }
        
        .solutions-text {
            font-size: var(--objectives-font-size);
            line-height: var(--objectives-line-height);
            color: #555;
            margin-bottom: 0.08in;
            padding: 0.05in;
        }

        .objectives-grid {
            display: flex;
            justify-content: space-around;
            gap: 0.08in;
            margin: 0.03in 0;
        }

        .objective-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
            max-width: 30%;
        }

        .objective-icon {
            width: 68px;
            height: 68px;
            background: transparent;
            border-radius: 0;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0.05in;
            box-shadow: none;
        }

        .objective-text-box {
            background: linear-gradient(135deg, #22559c 0%, #006bff 100%);
            color: white;
            padding: 0.08in 0.06in;
            border-radius: 12px;
            font-size: var(--objectives-font-size);
            line-height: 1.2;
            font-weight: 500;
            text-align: center;
            width: 100%;
            height: 0.8in;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(34,85,156,0.3);
            overflow: hidden;
            overflow-wrap: normal;
            word-break: normal;
            hyphens: none;
        }

        .service-icon-circle-small {
            width: 52px;
            height: 52px;
            background: #293238;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1rem;
            color: white;
            box-shadow: 0 2px 8px rgba(41,50,56,0.3);
            border: 2px solid rgba(255,255,255,0.1);
        }

        .service-icon-img-small {
            width: 60px;
            height: 60px;
            object-fit: contain;
        }
        
        .benefits-list {
            list-style: none;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            margin-left: -15px;
        }
        
        .benefits-list li {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.02in;
            padding-left: 0;
            padding: 0.05in 0;
        }
        
        .benefits-list li::before {
            display: none;
        }
        
        .benefit-number {
            width: calc(var(--outcomes-font-size) * 5.2);
            height: calc(var(--outcomes-font-size) * 5.2);
            margin-right: 0.1in;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            transform: translateY(2px);
        }
        
        .benefit-block-img {
            width: auto;
            height: calc(var(--outcomes-font-size) * 5.2);
            max-width: calc(var(--outcomes-font-size) * 5.2);
            object-fit: contain;
            image-rendering: auto;
        }
        
        .benefit-content h4 {
            font-size: var(--outcomes-font-size);
            font-weight: 600;
            color: #333;
            margin-bottom: 0.03in;
            line-height: var(--outcomes-line-height);
        }
        
        .benefit-content p {
            font-size: var(--outcomes-font-size);
            line-height: var(--outcomes-line-height);
            color: #555;
            margin-bottom: 0;
        }
        
        .footer {
            background: white;
            color: #333;
            padding: 0 0.3in;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-shrink: 0;
            height: 7%;
        }
        
        .footer-contact {
            display: flex;
            gap: 85px;
            align-items: center;
        }
        
        .footer-contact div {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .footer-contact span {
            font-size: 0.9rem;
            color: #555;
            line-height: 1;
            display: flex;
            align-items: center;
        }
        
        .footer-icon-img {
            height: 24px;
            width: auto;
            object-fit: contain;
            object-position: center;
            display: inline-block;
            vertical-align: middle;
            flex-shrink: 0;
            /* Maintain natural aspect ratio */
            max-height: 24px;
        }
        
        /* Make RISCPoint logo bigger */
        .footer-icon-img[alt="RISCPoint"] {
            height: 30px;
            max-height: 30px;
        }
        
        /* Improve vertical alignment of footer icons with text */
        .footer-contact div {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .footer-contact .icon {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        /* Placeholder styles for missing images */
        .placeholder-img {
            background: #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #999;
            font-size: 10px;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="container">
        <header class="header">
            <img src="${templateImagesBaseUrl}/RISCPoint-Shield-Logo-RegisteredTrademark-Black-4000px.png" alt="RISCPoint Logo" class="logo">
            <h1 data-preview-edit="title">${formatInlineBold(templateData.title)}</h1>
            <p class="subtitle" data-preview-edit="subtitle">${formatInlineBold(templateData.subtitle)}</p>
            <img src="${templateImagesBaseUrl}/Skyline-RPBlue-Cropped.png" alt="City Skyline" class="skyline">
        </header>

        <main class="main-content">
            <aside class="left-column">
${leftHtml}
            </aside>

            <section class="right-column">
${rightHtml}
            </section>
        </main>

        <footer class="footer">
            <div class="footer-contact">
                <div>
                    <div class="icon">
                        <img src="${templateImagesBaseUrl}/small_logo.png" alt="RISCPoint" class="footer-icon-img">
                    </div>
                    <span>www.RISCPoint.com</span>
                </div>
                <div>
                    <div class="icon">
                        <img src="${templateImagesBaseUrl}/email_logo.png" alt="Email" class="footer-icon-img">
                    </div>
                    <span>info@riscpoint.com</span>
                </div>
                <div>
                    <div class="icon">
                        <img src="${templateImagesBaseUrl}/phone_logo.png" alt="Phone" class="footer-icon-img">
                    </div>
                    <span>(888) 320-1327</span>
                </div>
            </div>
        </footer>
    </div>
</body>
</html>`;
};

export default renderPage1Template;
